// Anonymous, no-login "My Trips" — a small localStorage-backed list so a
// returning visitor can find itineraries they've viewed or created without
// needing an account. Safe to import from client components only.

const STORAGE_KEY = "aiTripMaker.myTrips";
const MAX_TRIPS = 12;

export interface MyTripEntry {
  city: string; // raw city param, e.g. "new york" or "paris"
  itinid: string; // "DEFAULT" or a persisted UUID
  cityName: string; // display name, e.g. "New York"
  visitedAt: string; // ISO timestamp of most recent visit
}

function readAll(): MyTripEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(trips: MyTripEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trips));
  } catch {
    // Storage full or unavailable (private browsing) — fail silently, this
    // is a convenience feature, not core functionality.
  }
}

export function getMyTrips(): MyTripEntry[] {
  return readAll().sort(
    (a, b) => new Date(b.visitedAt).getTime() - new Date(a.visitedAt).getTime()
  );
}

export function recordTripVisit(entry: Omit<MyTripEntry, "visitedAt">) {
  const existing = readAll();
  const key = (t: { city: string; itinid: string }) =>
    `${t.city.toLowerCase()}:${t.itinid}`;
  const withoutDuplicate = existing.filter((t) => key(t) !== key(entry));

  const next = [
    { ...entry, visitedAt: new Date().toISOString() },
    ...withoutDuplicate,
  ].slice(0, MAX_TRIPS);

  writeAll(next);
}

export function removeMyTrip(city: string, itinid: string) {
  const key = `${city.toLowerCase()}:${itinid}`;
  const next = readAll().filter(
    (t) => `${t.city.toLowerCase()}:${t.itinid}` !== key
  );
  writeAll(next);
}
